// @flow
import React, {Component} from 'react';
import './DataMap.css';
import Datamap from 'react-datamaps';
import Datamaps from 'datamaps';
import d3 from 'd3';
import _ from 'lodash';
import type {Candidate, CandidateSelect, DataSet, Datum, InputDatum, CensusData} from './types';
import {CENSUS_TX_MAP,
  COLOR_REPUBLICAN_MIN, COLOR_REPUBLICAN_MAX, COLOR_DEMOCRAT_MIN, COLOR_DEMOCRAT_MAX, COLOR_NEUTRAL} from './constants';

export default class DataMap extends Component {

  props: {
    data: DataSet,
    candidateSelected: CandidateSelect,
    censusData: CensusData
  };

  static defaultProps = {
    data: {},
    censusData: {},
  };

  selectScale(data: DataSet, candidateSelected: CandidateSelect) {
    if (!Object.keys(data).length) return data;
    if (candidateSelected !== 'both') {
      return makeCandidateScale(data, candidateSelected);
    }
    return makeBothScale(data);
  }

  render() {
    let data = this.selectScale(this.props.data, this.props.candidateSelected);
    data = _.mapValues(data, (datum, state) => ({
      ...datum,
      censusData: this.props.censusData[state]
    }));
    return (
      <div className="map-wrapper">
        <Datamap
          scope="usa"
          data={data}
          fills={{
            defaultFill: COLOR_NEUTRAL
          }}
          geographyConfig={{popupTemplate}}
        />
      </div>
    );
  }
}

function makeBothScale(data: DataSet): InputDatum {
  return makeScale(data, [COLOR_REPUBLICAN_MAX, COLOR_DEMOCRAT_MAX], (state) => state.hillary - state.trump);
}

function makeCandidateScale(data: DataSet, candidateSelected: Candidate): InputDatum {
  const palette = {
    hillary: [COLOR_DEMOCRAT_MIN, COLOR_DEMOCRAT_MAX],
    trump: [COLOR_REPUBLICAN_MIN, COLOR_REPUBLICAN_MAX]
  };
  return makeScale(data, palette[candidateSelected], (state) => state[candidateSelected]);
}

function makeScale(data: DataSet, colors: [string, string], scoreFn: (state: Datum) => number): InputDatum {
  const maxItem = _.maxBy(_.values(data), scoreFn);
  const max: number = scoreFn(maxItem);
  const paletteScale = makePaletteScale(max, colors);
  return _.mapValues(data, (state) => ({...state, fillColor: paletteScale(scoreFn(state))}));
}

function makePaletteScale(max: number, colors: []) {
  return d3
    .scale.linear()
    .domain([-max, max])
    .range(colors);
}

type Geography = {properties: {name: string}};
const IGNORE_KEYS = {'population_hshigher_i': 1, 'population_nohs_i': 1};
function popupTemplate(geography: Geography, data: Datum): string {
  const censusData = data.censusData || {};
  const censusRows = _.map(data.censusData, (v, k) =>
    IGNORE_KEYS[k] ? '' : `<td><b>${CENSUS_TX_MAP[k]}</b></td>
     <td>${v.toLocaleString()}</td>
     <td>${((v / censusData['totalpopulation_i']) * 100).toFixed(2)}%</td>
    `
  );
  const censusRowTpl = _.reduce(censusRows, (memo, val: string, i: number) => {
    return memo + (i % 2 === 0 ? '<tr>' : '') + val + (i % 2 === 1 ? '</tr>' : '');
  }, '');
  return `
    <div class="hoverinfo">
      <h2 class="header">${geography.properties.name}</h2>
      <h3 class="header">Ratings</h3>
      <table>
        <thead>
          <th>Hillary</th>
          <th>Trump</th>
        </thead>
        <tbody>
          <tr>
            <td>${data.hillary.toFixed(3)}</td>
            <td>${data.trump.toFixed(3)}</td>
          </tr>
        </tbody>
      </table>
      ${censusRows.length ?
        `<h3 class="header">Census Data</h3>
        <table class="census">
          <thead>
            <th>Item</th>
            <th>People</th>
            <th>Percent</th>
            <th>Item</th>
            <th>People</th>
            <th>Percent</th>
          </thead>
          <tbody>
            ${censusRowTpl}
          </tbody>
        </table>` : ''
      }
    </div>`;
}

// Monkey patch so this shows up properly on the right side
Datamaps.prototype.updatePopup = function(element, d, options) {
  const self = this;
  element.on('mousemove', null);
  element.on('mousemove', function() {
    const position = d3.mouse(self.options.element);
    const hoverover = d3.select(self.svg[0][0].parentNode).select('.datamaps-hoverover');
    const parentWidth = self.options.element.offsetWidth;
    const flip = position[0] > parentWidth / 2;
    hoverover
      .style('top', (position[1] + 30) + "px")
      .html(function() {
        const data = JSON.parse(element.attr('data-info'));
        try {
          return options.popupTemplate(d, data);
        } catch (e) {
          return "";
        }
      })
      .style('left', (position[0]) + "px")
      .style('right', 'auto');
    if (flip) {
      hoverover
        .style('left', 'auto')
        .style('right', (parentWidth - position[0]) + 'px');
    }
  });
  d3.select(self.svg[0][0].parentNode).select('.datamaps-hoverover').style('display', 'block');
};



/** WEBPACK FOOTER **
 ** ./src/DataMap.js
 **/